import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Mail, Lock, Eye, EyeOff, User, Phone, ArrowRight, Fingerprint, CheckCircle2, Globe } from "lucide-react";
import { useAuth, apiCall } from "../App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

const COUNTRY_CODES = [
  { code: "+91", country: "India", flag: "🇮🇳", short: "IN" },
  { code: "+1", country: "United States", flag: "🇺🇸", short: "US" },
  { code: "+44", country: "United Kingdom", flag: "🇬🇧", short: "GB" },
  { code: "+1", country: "Canada", flag: "🇨🇦", short: "CA" },
  { code: "+61", country: "Australia", flag: "🇦🇺", short: "AU" },
  { code: "+65", country: "Singapore", flag: "🇸🇬", short: "SG" },
  { code: "+971", country: "UAE", flag: "🇦🇪", short: "AE" },
  { code: "+49", country: "Germany", flag: "🇩🇪", short: "DE" },
  { code: "+31", country: "Netherlands", flag: "🇳🇱", short: "NL" },
  { code: "+64", country: "New Zealand", flag: "🇳🇿", short: "NZ" },
  { code: "+27", country: "South Africa", flag: "🇿🇦", short: "ZA" },
  { code: "+86", country: "China", flag: "🇨🇳", short: "CN" },
  { code: "+81", country: "Japan", flag: "🇯🇵", short: "JP" },
  { code: "+82", country: "South Korea", flag: "🇰🇷", short: "KR" },
  { code: "+33", country: "France", flag: "🇫🇷", short: "FR" },
  { code: "+39", country: "Italy", flag: "🇮🇹", short: "IT" },
  { code: "+34", country: "Spain", flag: "🇪🇸", short: "ES" },
  { code: "+55", country: "Brazil", flag: "🇧🇷", short: "BR" },
  { code: "+52", country: "Mexico", flag: "🇲🇽", short: "MX" },
  { code: "+60", country: "Malaysia", flag: "🇲🇾", short: "MY" },
  { code: "+66", country: "Thailand", flag: "🇹🇭", short: "TH" },
  { code: "+62", country: "Indonesia", flag: "🇮🇩", short: "ID" },
  { code: "+63", country: "Philippines", flag: "🇵🇭", short: "PH" },
  { code: "+234", country: "Nigeria", flag: "🇳🇬", short: "NG" },
  { code: "+254", country: "Kenya", flag: "🇰🇪", short: "KE" },
  { code: "+7", country: "Russia", flag: "🇷🇺", short: "RU" },
  { code: "+90", country: "Turkey", flag: "🇹🇷", short: "TR" },
  { code: "+966", country: "Saudi Arabia", flag: "🇸🇦", short: "SA" },
  { code: "+974", country: "Qatar", flag: "🇶🇦", short: "QA" },
  { code: "+973", country: "Bahrain", flag: "🇧🇭", short: "BH" },
  { code: "+968", country: "Oman", flag: "🇴🇲", short: "OM" },
  { code: "+965", country: "Kuwait", flag: "🇰🇼", short: "KW" },
  { code: "+977", country: "Nepal", flag: "🇳🇵", short: "NP" },
  { code: "+94", country: "Sri Lanka", flag: "🇱🇰", short: "LK" },
  { code: "+880", country: "Bangladesh", flag: "🇧🇩", short: "BD" },
  { code: "+92", country: "Pakistan", flag: "🇵🇰", short: "PK" },
  { code: "+41", country: "Switzerland", flag: "🇨🇭", short: "CH" },
  { code: "+46", country: "Sweden", flag: "🇸🇪", short: "SE" },
  { code: "+47", country: "Norway", flag: "🇳🇴", short: "NO" },
  { code: "+45", country: "Denmark", flag: "🇩🇰", short: "DK" },
  { code: "+353", country: "Ireland", flag: "🇮🇪", short: "IE" },
  { code: "+48", country: "Poland", flag: "🇵🇱", short: "PL" },
  { code: "+43", country: "Austria", flag: "🇦🇹", short: "AT" },
  { code: "+32", country: "Belgium", flag: "🇧🇪", short: "BE" },
  { code: "+351", country: "Portugal", flag: "🇵🇹", short: "PT" },
  { code: "+30", country: "Greece", flag: "🇬🇷", short: "GR" },
  { code: "+972", country: "Israel", flag: "🇮🇱", short: "IL" },
  { code: "+20", country: "Egypt", flag: "🇪🇬", short: "EG" },
  { code: "+852", country: "Hong Kong", flag: "🇭🇰", short: "HK" },
  { code: "+886", country: "Taiwan", flag: "🇹🇼", short: "TW" },
];

const Register = () => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [mobileNumber, setMobileNumber] = useState("");
  const [countryCode, setCountryCode] = useState("+91");
  const [showCodeDropdown, setShowCodeDropdown] = useState(false);
  const [codeSearch, setCodeSearch] = useState("");

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (!e.target.closest('[data-testid="country-code-btn"]') && !e.target.closest('[data-testid="country-code-dropdown"]')) {
        setShowCodeDropdown(false);
      }
    };
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);
  const [password, setPassword] = useState("");
  const [country, setCountry] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState("form"); // "form" -> "verify" -> done
  const [otp, setOtp] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [otpFallback, setOtpFallback] = useState("");
  const [emailVerified, setEmailVerified] = useState(false);
  const { register, user } = useAuth();
  const navigate = useNavigate();

  if (user) { navigate("/onboarding"); return null; }

  const handleSendVerifyOtp = async () => {
    if (!email) return toast.error("Enter your email first");
    setLoading(true);
    const res = await apiCall("POST", "/auth/send-register-otp", { email });
    setLoading(false);
    if (res.success) {
      setOtpSent(true);
      setOtpFallback(res.data.otp_fallback || "");
      setStep("verify");
      toast.success(res.data.email_sent !== false ? "Verification OTP sent to your email" : "OTP generated");
    } else {
      toast.error(res.error || "Failed to send OTP");
    }
  };

  const handleVerifyEmail = () => {
    if (!otp || otp.length < 6) return toast.error("Enter the 6-digit OTP");
    setEmailVerified(true);
    setStep("form");
    toast.success("Email verified!");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!emailVerified) {
      return handleSendVerifyOtp();
    }
    if (!country) {
      return toast.error("Please select your country");
    }
    setLoading(true);
    const res = await apiCall("POST", "/auth/register", { name, email, mobile: `${countryCode}${mobileNumber}`, password, otp, country });
    setLoading(false);
    if (res.success && res.data.token) {
      const { token, user: userData } = res.data;
      localStorage.setItem("siwon_token", token);
      toast.success("Your sacred journey begins!");
      // Force page reload to pick up new auth
      window.location.href = "/onboarding";
    } else {
      toast.error(res.error || "Registration failed");
    }
  };

  return (
    <div className="min-h-screen bg-cosmic-void flex items-center justify-center px-6 py-12" data-testid="register-page">
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
        <div className="w-[400px] h-[400px] rounded-full bg-divine-gold/5 blur-[80px]" />
      </div>

      <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="w-full max-w-md z-10">
        <div className="text-center mb-8">
          <Link to="/" className="inline-block">
            <h1 className="font-heading text-3xl text-divine-gold tracking-wider glow-text">SIWON HARMONY</h1>
          </Link>
          <p className="text-chakra-crown/60 mt-2 text-sm tracking-wide">Begin your sacred transformation</p>
        </div>

        <div className="sacred-card" data-testid="register-form">
          {/* Email Verification Step */}
          {step === "verify" && (
            <div className="space-y-4 mb-6">
              <div className="p-4 rounded-xl bg-emerald-500/10 border border-emerald-500/30 text-center">
                  <p className="text-emerald-400 text-sm font-heading">Verification OTP Sent</p>
                  <p className="text-chakra-crown/50 text-xs mt-1">Check your inbox at <span className="text-divine-gold">{email}</span></p>
                  {otpFallback && <p className="text-amber-400 text-xs mt-2">If you didn't receive the email, check spam or click "Resend OTP" below.</p>}
              </div>
              <div className="space-y-2">
                <Label className="label-sacred">Enter Verification OTP</Label>
                <Input value={otp} onChange={(e) => setOtp(e.target.value)} placeholder="6-digit OTP"
                  className="bg-neutral-obsidian/50 border border-divine-gold/20 text-chakra-crown text-center text-xl tracking-[0.2em]"
                  maxLength={6} data-testid="register-otp-input"
                  onKeyDown={(e) => e.key === "Enter" && handleVerifyEmail()} />
              </div>
              <Button onClick={handleVerifyEmail} className="w-full vedic-button" data-testid="verify-email-btn">
                Verify Email
              </Button>
              <button onClick={() => setStep("form")} className="text-divine-gold/60 hover:text-divine-gold text-xs w-full text-center">
                Back to form
              </button>
            </div>
          )}

          {/* Registration Form */}
          {step === "form" && (
            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="space-y-2">
                <Label className="label-sacred" htmlFor="name">Full Name</Label>
                <div className="relative">
                  <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-divine-gold/40" />
                  <Input id="name" type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder="Your full name"
                    className="pl-12 bg-neutral-obsidian/50 border border-divine-gold/20 focus:border-divine-gold text-chakra-crown placeholder:text-divine-gold/30"
                    required data-testid="name-input" />
                </div>
              </div>

              <div className="space-y-2">
                <Label className="label-sacred" htmlFor="email">Email</Label>
                <div className="relative">
                  <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-divine-gold/40" />
                  <Input id="email" type="email" value={email} onChange={(e) => { setEmail(e.target.value); setEmailVerified(false); }} placeholder="your@email.com"
                    className={`pl-12 pr-12 bg-neutral-obsidian/50 border ${emailVerified ? "border-emerald-500/50" : "border-divine-gold/20"} focus:border-divine-gold text-chakra-crown placeholder:text-divine-gold/30`}
                    required data-testid="email-input" />
                  {emailVerified ? (
                    <CheckCircle2 className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 text-emerald-400" />
                  ) : (
                    <button type="button" onClick={handleSendVerifyOtp} disabled={loading || !email}
                      className="absolute right-2 top-1/2 -translate-y-1/2 px-2 py-1 rounded-md bg-divine-gold/10 text-divine-gold text-xs hover:bg-divine-gold/20 transition-all disabled:opacity-30"
                      data-testid="verify-email-trigger">
                      {loading ? "..." : "Verify"}
                    </button>
                  )}
                </div>
                {emailVerified && <p className="text-emerald-400 text-xs flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> Email verified</p>}
              </div>

              <div className="space-y-2">
                <Label className="label-sacred" htmlFor="mobile">Mobile</Label>
                <div className="flex gap-0">
                  {/* Country Code Selector */}
                  <div className="relative" style={{ minWidth: "110px" }}>
                    <button type="button"
                      onClick={() => setShowCodeDropdown(!showCodeDropdown)}
                      className="flex items-center gap-1.5 h-10 px-3 rounded-l-lg border border-r-0 border-divine-gold/20 bg-neutral-obsidian/50 text-chakra-crown hover:border-divine-gold/40 transition-all w-full"
                      data-testid="country-code-btn">
                      <span className="text-base">{COUNTRY_CODES.find(c => c.code === countryCode)?.flag || "🌐"}</span>
                      <span className="text-sm text-divine-gold font-mono">{countryCode}</span>
                      <svg className="w-3 h-3 text-divine-gold/40 ml-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                    </button>
                    {showCodeDropdown && (
                      <div className="absolute z-50 top-full left-0 mt-1 w-64 bg-cosmic-deep border border-divine-gold/20 rounded-xl shadow-2xl overflow-hidden"
                        data-testid="country-code-dropdown">
                        <div className="p-2 border-b border-divine-gold/10">
                          <input type="text" value={codeSearch} onChange={e => setCodeSearch(e.target.value)}
                            placeholder="Search country..."
                            className="w-full px-3 py-1.5 rounded-lg bg-neutral-obsidian/50 border border-divine-gold/10 text-chakra-crown text-xs placeholder:text-divine-gold/30 outline-none focus:border-divine-gold/30"
                            autoFocus data-testid="country-code-search" />
                        </div>
                        <div className="max-h-48 overflow-y-auto">
                          {COUNTRY_CODES.filter(c =>
                            codeSearch === "" || c.country.toLowerCase().includes(codeSearch.toLowerCase()) || c.code.includes(codeSearch) || c.short.toLowerCase().includes(codeSearch.toLowerCase())
                          ).map(c => (
                            <button key={`${c.short}-${c.code}`} type="button"
                              onClick={() => { setCountryCode(c.code); setShowCodeDropdown(false); setCodeSearch(""); }}
                              className={`w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-divine-gold/10 transition-all ${countryCode === c.code ? "bg-divine-gold/5" : ""}`}
                              data-testid={`cc-option-${c.short}`}>
                              <span className="text-base">{c.flag}</span>
                              <span className="text-xs text-chakra-crown/80 flex-1 truncate">{c.country}</span>
                              <span className="text-xs text-divine-gold font-mono">{c.code}</span>
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                  {/* Mobile Number Input */}
                  <Input id="mobile" type="tel" value={mobileNumber}
                    onChange={(e) => setMobileNumber(e.target.value.replace(/[^0-9]/g, ""))}
                    placeholder="98765 43210"
                    className="rounded-l-none bg-neutral-obsidian/50 border border-divine-gold/20 focus:border-divine-gold text-chakra-crown placeholder:text-divine-gold/30 flex-1"
                    data-testid="mobile-input" />
                </div>
              </div>

              <div className="space-y-2">
                <Label className="label-sacred">Country *</Label>
                <div className="relative">
                  <Globe className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-divine-gold/40 z-10 pointer-events-none" />
                  <Select value={country} onValueChange={(val) => {
                    setCountry(val);
                    const match = COUNTRY_CODES.find(c => c.country === val);
                    if (match) setCountryCode(match.code);
                  }} required>
                    <SelectTrigger className="pl-12 bg-neutral-obsidian/50 border border-divine-gold/20 text-chakra-crown" data-testid="country-input">
                      <SelectValue placeholder="Select your country" />
                    </SelectTrigger>
                    <SelectContent className="bg-cosmic-deep border border-divine-gold/20 max-h-60">
                      <SelectItem value="India">India</SelectItem>
                      <SelectItem value="United States">United States</SelectItem>
                      <SelectItem value="United Kingdom">United Kingdom</SelectItem>
                      <SelectItem value="Canada">Canada</SelectItem>
                      <SelectItem value="Australia">Australia</SelectItem>
                      <SelectItem value="Singapore">Singapore</SelectItem>
                      <SelectItem value="UAE">UAE</SelectItem>
                      <SelectItem value="Germany">Germany</SelectItem>
                      <SelectItem value="Netherlands">Netherlands</SelectItem>
                      <SelectItem value="New Zealand">New Zealand</SelectItem>
                      <SelectItem value="South Africa">South Africa</SelectItem>
                      <SelectItem value="Other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label className="label-sacred" htmlFor="password">Password</Label>
                <div className="relative">
                  <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-divine-gold/40" />
                  <Input id="password" type={showPassword ? "text" : "password"} value={password} onChange={(e) => setPassword(e.target.value)}
                    placeholder="Create a strong password"
                    className="pl-12 pr-12 bg-neutral-obsidian/50 border border-divine-gold/20 focus:border-divine-gold text-chakra-crown placeholder:text-divine-gold/30"
                    required minLength={6} data-testid="password-input" />
                  <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-4 top-1/2 -translate-y-1/2 text-divine-gold/40 hover:text-divine-gold">
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              <Button type="submit" disabled={loading} className="w-full vedic-button flex items-center justify-center gap-2 mt-6" data-testid="register-submit-btn">
                {loading ? <span className="animate-pulse">Creating Your Space...</span> : (
                  emailVerified ? <><ArrowRight className="w-5 h-5" /> Begin Sacred Journey</> : <><Fingerprint className="w-5 h-5" /> Verify Email & Register</>
                )}
              </Button>
            </form>
          )}

          <div className="flex items-center my-6">
            <div className="flex-1 h-px bg-divine-gold/20" />
            <span className="px-4 text-chakra-crown/40 text-sm">or</span>
            <div className="flex-1 h-px bg-divine-gold/20" />
          </div>

          <p className="text-center text-chakra-crown/60">
            Already on the path?{" "}
            <Link to="/login" className="text-divine-gold hover:text-divine-gold-light transition-colors font-medium" data-testid="login-link">
              Return to Login
            </Link>
          </p>
        </div>
      </motion.div>
    </div>
  );
};

export default Register;
